import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { OwnershipLevel } from '@fitness-force/enum';
import { AppErrors, BadRequestError, NotFoundError } from '@fitness-force/errors';
import { EmailTemplateMasterById, EmailTemplateMasterResponse } from '@fitness-force/models';
import { environment } from 'apps/ff-frontend/src/environments/environment';
import { EmailFromMasterResponse } from 'libs/models/src/lib/notifications/emails/emailfrommaster';
import { throwError, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TemplateManagementService {
  constructor(private http: HttpClient, private router: Router) {}

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };

  getEmailFromConfig(tenantId: number): Observable<any> {
    return this.http
      .get(
        `${environment.BASE_URL}/tenants/${tenantId}/email_from_config_master`,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }
  getSingleEmailFromConfig(
    tenantId: number,
    fromEmailId: number
  ): Observable<any> {
    return this.http
      .get(
        `${environment.BASE_URL}/tenants/${tenantId}/email_from_config_master/${fromEmailId}`,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createProviderConfig(
    tenantId: number,
    email_provider_config_master: number,
    data: any
  ) {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants/${tenantId}/email_provider_config_master/${email_provider_config_master}/email_from_config_master`,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateProviderConfig(
    tenantId: number,
    email_provider_config_master: number,
    emailFromId: number,
    data: any
  ) {
    return this.http
      .put(
        `${environment.BASE_URL}/tenants/${tenantId}/email_provider_config_master/${email_provider_config_master}/email_from_config_master/${emailFromId}`,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getEmailProviderConfig(tenantId: number): Observable<any> {
    return this.http
      .get(
        `${environment.BASE_URL}/tenants/${tenantId}/email_provider_config_master`,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getEmailTemplate(tenantId: number): Observable<any> {
    return this.http
      .get(
        `${environment.BASE_URL}/tenants/${tenantId}/email_template_master`,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getSingleEmailTemplate(
    tenantId: number,
    templateId: number
  ): Observable<any> {
    return this.http
      .get(
        `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${templateId}`,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createEmailTemplate(ownership:OwnershipLevel, payload:any, tenantId:number, franchiseGroupId:number) {

    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = `${environment.BASE_URL}/companies/email_template_master`;
        break;
      case OwnershipLevel.Franchise:
        url = `${environment.BASE_URL}/franchise_group/${franchiseGroupId}/email_template_master`;
        break;
      case OwnershipLevel.Tenant:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master`;
        break;
      default:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master`
        break;
    }

    return this.http
      .post(
        url,
        payload,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateEmailTemplate(ownership:OwnershipLevel, templateId:number, updatePayload:any, tenantId:number, franchiseGroupId:number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = `${environment.BASE_URL}/companies/email_template_master/${templateId}`;
        break;
      case OwnershipLevel.Franchise:
        url = `${environment.BASE_URL}/franchise_group/${franchiseGroupId}/email_template_master/${templateId}`;
        break;
      case OwnershipLevel.Tenant:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${templateId}`;
        break;
      default:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${templateId}`
        break;
    }

    return this.http.put(url,updatePayload,this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getEmailNotifications(tenantId: number): Observable<any> {
    return this.http
      .get(
        `${environment.BASE_URL}/tenants/${tenantId}/notifications`,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res.items;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  // TODO fix with real API when available ...
  updateEmailFrom(tenantId: number, fromId: number, data: any) {
    return this.http
      .put(
        `${environment.BASE_URL}/tenants/${tenantId}/service_categories/${fromId}`,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActiveFromEmail(
    tenantId: number,
    emailProvider: number,
    emailFrom: number
  ) {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants/${tenantId}/email_provider_config_master_id/${emailProvider}/email_from_config_master/${emailFrom}/mark_as_active`,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveFromEmail(
    tenantId: number,
    emailProvider: number,
    emailFrom: number
  ) {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants/${tenantId}/email_provider_config_master_id/${emailProvider}/email_from_config_master/${emailFrom}/mark_as_inactive`,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markActiveTemplate(tenantId: number, email_template_master_id: number) {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${email_template_master_id}/mark_as_active`,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveTemplate(tenantId: number, email_template_master_id: number) {
    return this.http
      .post(
        `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${email_template_master_id}/mark_as_inactive`,
        {},
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActiveTemplateByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number, email_template_master_id: number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = `${environment.BASE_URL}/companies/email_template_master/${email_template_master_id}/mark_as_inactive`;
        break;
      case OwnershipLevel.Franchise:
        url = `${environment.BASE_URL}/franchise_group/${franchiseGroupId}/email_template_master/${email_template_master_id}/mark_as_inactive`;
        break;
      case OwnershipLevel.Tenant:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${email_template_master_id}/mark_as_inactive`;
        break;
      default:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${email_template_master_id}/mark_as_inactive`;
        break;
    }
    return this.http.post(url, {}, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  } 

  markActiveTemplateByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number, email_template_master_id: number) {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = `${environment.BASE_URL}/companies/email_template_master/${email_template_master_id}/mark_as_active`;
        break;
      case OwnershipLevel.Franchise:
        url = `${environment.BASE_URL}/franchise_group/${franchiseGroupId}/email_template_master/${email_template_master_id}/mark_as_active`;
        break;
      case OwnershipLevel.Tenant:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${email_template_master_id}/mark_as_active`;
        break;
      default:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${email_template_master_id}/mark_as_active`;
        break;
    }
    return this.http.post(url, {}, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  } 


  // neeed to remove its duplicate from notification service
  getEmailTemplatesByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number): Observable<EmailTemplateMasterResponse> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = `${environment.BASE_URL}/companies/email_template_master`;
        break;
      case OwnershipLevel.Franchise:
        url = `${environment.BASE_URL}/franchise_group/${franchiseGroupId}/email_template_master`;
        break;
      case OwnershipLevel.Tenant:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master`;
        break;
      default:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master`;
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getEmailTemplateByIdWithOwnership(ownership:OwnershipLevel, templateId:number,franchiseGroupId:number,tenantId:number): Observable<EmailTemplateMasterById> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = `${environment.BASE_URL}/companies/email_template_master/${templateId}`
        break;
      case OwnershipLevel.Franchise:
        url = `${environment.BASE_URL}/franchise_group/${franchiseGroupId}/email_template_master/${templateId}`;
        break;
      case OwnershipLevel.Tenant:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${templateId}`
        break;
      default:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_template_master/${templateId}`
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getSendEmailFromListByOwnership(ownership:OwnershipLevel, tenantId:number, franchiseGroupId:number): Observable<EmailFromMasterResponse> {
    let url="";
    switch (ownership) {
      case OwnershipLevel.Company:
        url = `${environment.BASE_URL}/companies/email_from_config_master`;
        break;
      case OwnershipLevel.Franchise:
        url = `${environment.BASE_URL}/franchise_group/${franchiseGroupId}/email_from_config_master`;
        break;
      case OwnershipLevel.Tenant:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_from_config_master`
        break;
      default:
        url = `${environment.BASE_URL}/tenants/${tenantId}/email_from_config_master`
        break;
    }
    return this.http.get(url, {
      headers: this.httpOptions.headers
    }).pipe(
      map((res: any) => {
        return res
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        } else if (error.status === 400) {
          return throwError(() => new BadRequestError(error))
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }
}
